// Helpers for building table context menu items and basic table-related utilities
import {
  clearSuggestionMarksInRange,
  createSuggestionMarkAttrs,
  getTableSuggestionContext
} from './suggestionHelpers';

const normalizeDiffMode = (value) => String(value || '').trim().toLowerCase() === 'suggest' ? 'suggest' : 'write';

const isSuggestMode = (options = {}) => normalizeDiffMode(options?.diffMode) === 'suggest';

const clampDocPos = (doc, pos) => {
  const max = Number(doc?.content?.size || 0);
  const numeric = Number(pos || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(max, numeric));
};

const focusTableAnchor = (editor, anchorEl) => {
  try {
    if (!editor || !editor.view || !anchorEl || !editor.chain) return null;
    const view = editor.view;
    const anchorNode = (anchorEl.nodeName && anchorEl.nodeName.toUpperCase() === 'TABLE')
      ? (anchorEl.querySelector('td,th') || anchorEl)
      : anchorEl;
    let pos = null;
    try {
      const rect = anchorNode?.getBoundingClientRect?.();
      if (rect && Number.isFinite(rect.left) && Number.isFinite(rect.top)) {
        const coords = view.posAtCoords({
          left: rect.left + Math.max(4, rect.width / 2),
          top: rect.top + Math.max(4, rect.height / 2)
        });
        if (coords && typeof coords.pos === 'number') {
          pos = coords.pos;
        }
      }
      if (pos == null && anchorNode) {
        pos = view.posAtDOM(anchorNode, 0);
      }
    } catch (err) {
      pos = null;
    }

    const focusChain = editor.chain().focus();
    if (typeof pos === 'number' && Number.isFinite(pos)) {
      try { focusChain.setTextSelection(pos); } catch (err) {}
    }
    focusChain.run();
    return true;
  } catch (err) {
    return false;
  }
};

const isSameTableContext = (a, b) => {
  if (!a || !b) return false;
  return a === b || (typeof a.isSameNode === 'function' && a.isSameNode(b));
};

const resolveTableElement = (anchorEl) => {
  if (!anchorEl || typeof anchorEl !== 'object') return null;
  const nodeName = String(anchorEl.nodeName || '').toUpperCase();
  if (nodeName === 'TABLE') return anchorEl;
  return anchorEl.closest ? anchorEl.closest('table') : null;
};

const resolveSelectionTableElement = (editor) => {
  try {
    if (!editor?.view || !editor?.state?.selection) return null;
    const from = editor.state.selection.from;
    if (!Number.isFinite(from)) return null;
    const domAtPos = editor.view.domAtPos(from);
    const node = domAtPos?.node;
    const el = node?.nodeType === 3 ? node.parentElement : node;
    return el?.closest ? el.closest('table') : null;
  } catch (e) {
    return null;
  }
};

const getAnchorPos = (editor, anchorEl) => {
  try {
    const view = editor?.view;
    if (!view) return null;
    const anchorNode = (anchorEl?.nodeName && anchorEl.nodeName.toUpperCase() === 'TABLE')
      ? (anchorEl.querySelector('td,th') || anchorEl)
      : anchorEl;
    if (anchorNode) {
      const pos = view.posAtDOM(anchorNode, 0);
      if (Number.isFinite(pos)) return pos;
    }
  } catch (err) {}
  const fallback = Number(editor?.state?.selection?.from);
  return Number.isFinite(fallback) ? fallback : null;
};

const getAncestorNodeRangeByNames = (state, pos, names = []) => {
  const doc = state?.doc;
  const nameSet = new Set((Array.isArray(names) ? names : []).map((name) => String(name || '').trim()));
  if (!doc || !nameSet.size || !Number.isFinite(pos)) return null;
  let $pos;
  try {
    $pos = doc.resolve(clampDocPos(doc, pos));
  } catch (err) {
    return null;
  }
  if (!$pos || typeof $pos.depth !== 'number' || typeof $pos.node !== 'function') return null;

  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    const name = String(node?.type?.name || '').trim();
    if (!nameSet.has(name)) continue;
    const from = Number($pos.before(depth));
    const to = Number($pos.after(depth));
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) continue;
    return {
      from,
      to,
      name,
      attrs: node?.attrs || {}
    };
  }
  return null;
};

const buildStructuralSuggestionAttrs = ({ currentUserId = null, op = 'remove' } = {}) => {
  return createSuggestionMarkAttrs({
    op,
    source: 'user_suggest',
    authorId: currentUserId,
    createdAt: Date.now(),
    status: 'pending'
  });
};

const applyNodeSuggestion = (state, dispatch, ranges = [], attrs = {}, op = 'remove') => {
  if (!state || typeof dispatch !== 'function' || !Array.isArray(ranges) || !ranges.length) return false;
  const normalizedOp = String(op || '').trim().toLowerCase() === 'add' ? 'add' : 'remove';
  const tr = state.tr;
  const sortedRanges = [...ranges]
    .filter((range) => Number.isFinite(range?.from) && Number.isFinite(range?.to) && range.to > range.from)
    .sort((a, b) => b.from - a.from || b.to - a.to);

  for (const range of sortedRanges) {
    try {
      clearSuggestionMarksInRange(tr, state, range.from, range.to);
      const node = tr.doc.nodeAt(range.from);
      if (!node) continue;
      tr.setNodeMarkup(range.from, undefined, {
        ...(node.attrs || {}),
        diffOp: normalizedOp,
        diffAnnotationId: attrs.annotation_id || null,
        diffBatchId: attrs.batch_id || null
      });
    } catch (err) {}
  }

  if (!tr.docChanged) return false;
  dispatch(tr.scrollIntoView());
  return true;
};

const collectColumnNodeRanges = (editor, tableEl, colIndex) => {
  const ranges = [];
  const seen = new Set();
  if (!editor?.state || !editor?.view || !tableEl || !Number.isInteger(colIndex) || colIndex < 0) return ranges;
  const rows = Array.from(tableEl.rows || []);
  rows.forEach((rowEl) => {
    const cells = Array.from(rowEl.children || []).filter(node => node.tagName === 'TD' || node.tagName === 'TH');
    const cell = cells[colIndex] || null;
    if (!cell) return;
    let pos = null;
    try {
      pos = editor.view.posAtDOM(cell, 0);
    } catch (err) {
      pos = null;
    }
    if (!Number.isFinite(pos)) return;
    const range = getAncestorNodeRangeByNames(editor.state, pos, ['tableCell', 'tableHeader']);
    if (!range) return;
    const key = `${range.from}:${range.to}`;
    if (seen.has(key)) return;
    seen.add(key);
    ranges.push(range);
  });
  return ranges;
};

const runSuggestDeleteTable = (editor, anchorEl, options = {}) => {
  const state = editor?.state;
  const dispatch = editor?.view?.dispatch;
  if (!state || typeof dispatch !== 'function') return false;

  const selection = state.selection;
  const context = getTableSuggestionContext(state, Number(selection?.from || 0), Number(selection?.to || 0));
  const tableRange = context?.type === 'table'
    ? { from: context.from, to: context.to }
    : getAncestorNodeRangeByNames(state, getAnchorPos(editor, anchorEl), ['table']);
  if (!tableRange) return false;

  const attrs = buildStructuralSuggestionAttrs({ currentUserId: options?.currentUserId, op: 'remove' });
  return applyNodeSuggestion(state, dispatch, [tableRange], attrs, 'remove');
};

const runSuggestDeleteRow = (editor, anchorEl, options = {}) => {
  const state = editor?.state;
  const dispatch = editor?.view?.dispatch;
  if (!state || typeof dispatch !== 'function') return false;

  const selection = state.selection;
  const context = getTableSuggestionContext(state, Number(selection?.from || 0), Number(selection?.to || 0));
  const rowRanges = context?.type === 'rows'
    ? (context.rowRanges || [])
    : [getAncestorNodeRangeByNames(state, getAnchorPos(editor, anchorEl), ['tableRow'])].filter(Boolean);
  if (!rowRanges.length) return false;

  const attrs = buildStructuralSuggestionAttrs({ currentUserId: options?.currentUserId, op: 'remove' });
  return applyNodeSuggestion(state, dispatch, rowRanges, attrs, 'remove');
};

const runSuggestDeleteColumn = (editor, anchorEl, options = {}) => {
  const state = editor?.state;
  const dispatch = editor?.view?.dispatch;
  if (!state || typeof dispatch !== 'function') return false;
  const tableEl = resolveTableElement(anchorEl) || resolveSelectionTableElement(editor);
  const anchorPosition = getAnchorCellPosition(anchorEl);
  const colIndex = Number(anchorPosition?.colIndex);
  if (!tableEl || !Number.isInteger(colIndex) || colIndex < 0) return false;

  const ranges = collectColumnNodeRanges(editor, tableEl, colIndex);
  if (!ranges.length) return false;

  const attrs = buildStructuralSuggestionAttrs({ currentUserId: options?.currentUserId, op: 'remove' });
  return applyNodeSuggestion(state, dispatch, ranges, attrs, 'remove');
};

const markInsertedRowAsSuggestionAdd = (editor, tableEl, rowIndex, options = {}) => {
  const state = editor?.state;
  const dispatch = editor?.view?.dispatch;
  if (!state || typeof dispatch !== 'function' || !tableEl || !Number.isInteger(rowIndex) || rowIndex < 0) return false;
  const rows = Array.from(tableEl.rows || []);
  const rowEl = rows[rowIndex] || null;
  const firstCell = rowEl?.querySelector?.('td,th') || null;
  if (!firstCell) return false;

  let pos = null;
  try {
    pos = editor.view.posAtDOM(firstCell, 0);
  } catch (err) {
    pos = null;
  }
  if (!Number.isFinite(pos)) return false;
  const rowRange = getAncestorNodeRangeByNames(state, pos, ['tableRow']);
  if (!rowRange) return false;

  const attrs = buildStructuralSuggestionAttrs({ currentUserId: options?.currentUserId, op: 'add' });
  return applyNodeSuggestion(state, dispatch, [rowRange], attrs, 'add');
};

const markInsertedColumnAsSuggestionAdd = (editor, tableEl, colIndex, options = {}) => {
  const state = editor?.state;
  const dispatch = editor?.view?.dispatch;
  if (!state || typeof dispatch !== 'function' || !tableEl || !Number.isInteger(colIndex) || colIndex < 0) return false;
  const ranges = collectColumnNodeRanges(editor, tableEl, colIndex);
  if (!ranges.length) return false;
  const attrs = buildStructuralSuggestionAttrs({ currentUserId: options?.currentUserId, op: 'add' });
  return applyNodeSuggestion(state, dispatch, ranges, attrs, 'add');
};

const getAnchorCellPosition = (anchorEl) => {
  const cell = anchorEl?.closest ? anchorEl.closest('td,th') : null;
  if (!cell) return null;
  const row = cell.parentElement;
  const table = row?.closest ? row.closest('table') : null;
  const rowIndex = Array.from(table?.rows || []).indexOf(row);
  const cells = Array.from(row?.children || []).filter(node => node.tagName === 'TD' || node.tagName === 'TH');
  const colIndex = cells.indexOf(cell);
  if (rowIndex < 0 || colIndex < 0) return null;
  return { rowIndex, colIndex };
};

const focusCellAtPosition = (editor, tableEl, position) => {
  try {
    if (!editor || !editor.view || !tableEl) return;
    const rows = Array.from(tableEl.rows || []);
    if (!rows.length) return;

    const desiredRow = Number.isInteger(position?.rowIndex) ? position.rowIndex : 0;
    const safeRowIndex = Math.max(0, Math.min(desiredRow, rows.length - 1));
    const row = rows[safeRowIndex];
    const cells = Array.from(row?.children || []).filter(node => node.tagName === 'TD' || node.tagName === 'TH');
    if (!cells.length) return;

    const desiredCol = Number.isInteger(position?.colIndex) ? position.colIndex : 0;
    const safeColIndex = Math.max(0, Math.min(desiredCol, cells.length - 1));
    const target = cells[safeColIndex] || cells[cells.length - 1] || null;
    if (!target) return;
    let pos = null;
    const rect = target.getBoundingClientRect?.();
    if (rect && Number.isFinite(rect.left) && Number.isFinite(rect.top)) {
      const coords = editor.view.posAtCoords({
        left: rect.left + Math.max(4, rect.width / 2),
        top: rect.top + Math.max(4, rect.height / 2)
      });
      if (coords && typeof coords.pos === 'number') {
        pos = coords.pos;
      }
    }
    if (pos == null) {
      pos = editor.view.posAtDOM(target, 0);
    }
    if (!Number.isFinite(pos)) return;
    editor.chain().focus().setTextSelection(pos).run();
  } catch (e) {}
};

const runTableCommand = (editor, anchorEl, commandOrName, options = {}) => {
  const isSuggest = isSuggestMode(options);

  const execute = () => {
    try {
      if (typeof commandOrName === 'string') {
        const direct = editor?.commands?.[commandOrName];
        if (typeof direct === 'function') {
          const directResult = Boolean(direct());
          if (directResult) return true;

          const chain = editor?.chain?.().focus();
          const chained = chain?.[commandOrName];
          if (typeof chained === 'function') {
            chained.call(chain);
            return Boolean(chain.run());
          }

          return false;
        }
      }
    } catch (e) {}

    if (typeof commandOrName === 'function') {
      const chain = editor.chain().focus();
      commandOrName(chain);
      return chain.run();
    }

    return false;
  };

  const finalize = (tableEl, anchorIndex) => {
    if (isSuggest && options?.markInsertedAsAdd && tableEl) {
      setTimeout(() => {
        const currentTable = resolveTableElement(anchorEl) || resolveSelectionTableElement(editor) || tableEl;
        const currentAnchor = getAnchorCellPosition(anchorEl) || anchorIndex || { rowIndex: 0, colIndex: 0 };
        const insertionType = String(options?.markInsertedAsAdd || '').trim().toLowerCase();
        if (insertionType === 'row') {
          const insertedRowIndex = options?.insertedIndexResolver
            ? options.insertedIndexResolver(currentAnchor)
            : currentAnchor.rowIndex;
          markInsertedRowAsSuggestionAdd(editor, currentTable, Number(insertedRowIndex), options);
        }
        if (insertionType === 'column') {
          const insertedColIndex = options?.insertedIndexResolver
            ? options.insertedIndexResolver(currentAnchor)
            : currentAnchor.colIndex;
          markInsertedColumnAsSuggestionAdd(editor, currentTable, Number(insertedColIndex), options);
        }
      }, 0);
    }

    if (options?.refocusAfter) {
      setTimeout(() => {
        const currentTable = resolveTableElement(anchorEl) || resolveSelectionTableElement(editor) || tableEl;
        if (options?.refocusAfter && currentTable) {
          const nextPosition = typeof options.focusPositionAfter === 'function'
            ? options.focusPositionAfter(anchorIndex)
            : anchorIndex;
          focusCellAtPosition(editor, currentTable, nextPosition);
        }
      }, 0);
    }
  };

  try {
    if (!editor || (!commandOrName || (typeof commandOrName !== 'function' && typeof commandOrName !== 'string'))) return;

    if (isSuggest) {
      const normalizedCommand = String(commandOrName || '').trim();
      if (normalizedCommand === 'deleteTable') {
        return runSuggestDeleteTable(editor, anchorEl, options);
      }
      if (normalizedCommand === 'deleteRow') {
        return runSuggestDeleteRow(editor, anchorEl, options);
      }
      if (normalizedCommand === 'deleteColumn') {
        return runSuggestDeleteColumn(editor, anchorEl, options);
      }
    }

    const tableEl = resolveTableElement(anchorEl);
    const anchorIndex = getAnchorCellPosition(anchorEl) || { rowIndex: 0, colIndex: 0 };
    const selectionTable = resolveSelectionTableElement(editor);
    let didRun = false;

    if (isSameTableContext(selectionTable, tableEl)) {
      didRun = execute();
    }

    if (!didRun) {
      focusTableAnchor(editor, anchorEl);
      didRun = execute();
    }

    if (!didRun) return false;
    finalize(tableEl, anchorIndex);
    return true;
  } catch (err) {
    try {
      const tableEl = resolveTableElement(anchorEl);
      const anchorIndex = getAnchorCellPosition(anchorEl) || { rowIndex: 0, colIndex: 0 };
      focusTableAnchor(editor, anchorEl);
      const didRun = execute();
      if (!didRun) return false;
      finalize(tableEl, anchorIndex);
      return true;
    } catch (e) {}
  }
  return false;
};

export function buildTableMenuItems(editor, onClose = () => {}, anchorEl = null, options = {}) {
  const safeRun = (fn) => {
    try {
      fn?.();
    } catch (err) {
      // Swallow: editor may not expose the command.
    }
    try {
      onClose?.();
    } catch (e) {}
  };

  return [
    {
      label: 'Insert row above',
      iconClass: 'rows-plus-top',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'addRowBefore', {
        ...options,
        markInsertedAsAdd: isSuggestMode(options) ? 'row' : null,
        insertedIndexResolver: (anchorPosition) => Math.max(0, anchorPosition?.rowIndex ?? 0),
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, (anchorPosition?.rowIndex ?? 0) + 1),
          colIndex: Math.max(0, anchorPosition?.colIndex ?? 0)
        })
      })),
    },
    {
      label: 'Insert row below',
      iconClass: 'rows-plus-bottom',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'addRowAfter', {
        ...options,
        markInsertedAsAdd: isSuggestMode(options) ? 'row' : null,
        insertedIndexResolver: (anchorPosition) => Math.max(0, (anchorPosition?.rowIndex ?? 0) + 1),
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, anchorPosition?.rowIndex ?? 0),
          colIndex: Math.max(0, anchorPosition?.colIndex ?? 0)
        })
      })),
    },
    {
      label: 'Insert column left',
      iconClass: 'columns-plus-left',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'addColumnBefore', {
        ...options,
        markInsertedAsAdd: isSuggestMode(options) ? 'column' : null,
        insertedIndexResolver: (anchorPosition) => Math.max(0, anchorPosition?.colIndex ?? 0),
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, anchorPosition?.rowIndex ?? 0),
          colIndex: Math.max(0, (anchorPosition?.colIndex ?? 0) + 1)
        })
      })),
    },
    {
      label: 'Insert column right',
      iconClass: 'columns-plus-right',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'addColumnAfter', {
        ...options,
        markInsertedAsAdd: isSuggestMode(options) ? 'column' : null,
        insertedIndexResolver: (anchorPosition) => Math.max(0, (anchorPosition?.colIndex ?? 0) + 1),
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, anchorPosition?.rowIndex ?? 0),
          colIndex: Math.max(0, anchorPosition?.colIndex ?? 0)
        })
      })),
    },
    { divider: true },
    {
      label: 'Delete row',
      iconClass: 'rows-delete',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'deleteRow', {
        ...options,
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, anchorPosition?.rowIndex ?? 0),
          colIndex: Math.max(0, anchorPosition?.colIndex ?? 0)
        })
      })),
    },
    {
      label: 'Delete column',
      iconClass: 'columns-delete',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'deleteColumn', {
        ...options,
        refocusAfter: true,
        focusPositionAfter: (anchorPosition) => ({
          rowIndex: Math.max(0, anchorPosition?.rowIndex ?? 0),
          colIndex: Math.max(0, anchorPosition?.colIndex ?? 0)
        })
      })),
    },
    {
      label: 'Delete table',
      iconClass: 'trash',
      onClick: () => safeRun(() => runTableCommand(editor, anchorEl, 'deleteTable', {
        ...options
      })),
    },
  ];
}

export function getClosestTableElement(startEl) {
  let el = startEl;
  while (el && el !== document.body) {
    const name = el.nodeName && el.nodeName.toUpperCase();
    if (name === 'TD' || name === 'TH' || name === 'TABLE') return el;
    el = el.parentElement;
  }
  return null;
}

